import type { RefObject } from 'react'
import { FileText, Loader2, Trash2, Upload } from 'lucide-react'
import { documentStatusLabel, formatDocDateTime, humanizeDocError } from '@/lib/documents-ui'
import type { ProjectDoc } from '@/pages/project/project-utils'

type ProjectDocumentsSectionProps = {
  projectId: string
  docs: ProjectDoc[]
  docsLoading: boolean
  docsUploading: boolean
  docsError: string | null
  deletingDocId: string | null
  activeJobId: string | null
  acceptAttr: string
  maxUploadMb: number
  uploadInputRef: RefObject<HTMLInputElement | null>
  onUploadProjectDocs: (files: FileList | null) => void
  onRefreshDocs: () => void
  onDeleteDoc: (docId: string) => void
}

export function ProjectDocumentsSection({
  projectId,
  docs,
  docsLoading,
  docsUploading,
  docsError,
  deletingDocId,
  activeJobId,
  acceptAttr,
  maxUploadMb,
  uploadInputRef,
  onUploadProjectDocs,
  onRefreshDocs,
  onDeleteDoc,
}: ProjectDocumentsSectionProps) {
  return (
    <div className="glass-surface" style={{ padding: '12px' }}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-semibold inline-flex items-center gap-1">
          <FileText size={13} className="text-telegram-button" />
          Общие документы проекта
        </p>
        <button
          onClick={onRefreshDocs}
          className="text-[10px] text-telegram-button active:scale-95 transition-transform"
          disabled={docsLoading}
        >
          {docsLoading ? 'Обновляю…' : 'Обновить'}
        </button>
      </div>
      <p className="text-[10px] text-telegram-hint mb-2">
        Документы сохраняются с `contract_id = {projectId}` • Разрешено: {acceptAttr} • до {maxUploadMb} MB
      </p>
      <input
        ref={uploadInputRef}
        type="file"
        multiple
        accept={acceptAttr}
        className="hidden"
        onChange={(event) => onUploadProjectDocs(event.target.files)}
        disabled={docsUploading}
      />
      <button
        onClick={() => uploadInputRef.current?.click()}
        disabled={docsUploading}
        className="rounded-xl bg-telegram-button text-white w-full h-[36px] text-[11px] font-semibold inline-flex items-center justify-center gap-1 active:scale-95 transition-transform disabled:opacity-40"
      >
        {docsUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
        {docsUploading ? 'Загрузка…' : 'Загрузить документ в проект'}
      </button>

      {docsError && (
        <p className="text-[10px] text-red-500 mt-2">{docsError}</p>
      )}
      {activeJobId && (
        <p className="text-[10px] text-telegram-button mt-2">Идёт индексация документов…</p>
      )}

      <div className="mt-2 flex flex-col gap-1">
        {docs.length === 0 && !docsLoading && (
          <p className="text-[11px] text-telegram-hint">Документы проекта пока не загружены</p>
        )}
        {docs.map((doc) => {
          const deleting = deletingDocId === doc.id
          return (
            <div key={doc.id} className="rounded-lg glass-icon px-2 py-1.5">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium truncate">{doc.filename}</p>
                  <p className="text-[10px] text-telegram-hint">
                    {doc.scope || 'contract'}
                    {typeof doc.size_bytes === 'number' ? ` • ${Math.round(doc.size_bytes / 1024)} KB` : ''}
                    {formatDocDateTime(doc.created_at) ? ` • ${formatDocDateTime(doc.created_at)}` : ''}
                  </p>
                  {humanizeDocError(doc.error_message) && (
                    <p className="text-[10px] text-red-500 truncate">{humanizeDocError(doc.error_message)}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span
                    className="rounded-full px-2 py-0.5 text-[9px]"
                    style={{
                      background: String(doc.status || '').toLowerCase() === 'ready'
                        ? 'var(--tg-theme-button-color, #2481cc)'
                        : 'rgba(127,127,127,0.2)',
                      color: String(doc.status || '').toLowerCase() === 'ready'
                        ? 'var(--tg-theme-button-text-color, #fff)'
                        : 'var(--tg-theme-hint-color, #999)',
                    }}
                  >
                    {documentStatusLabel(doc.status)}
                  </span>
                  <button
                    onClick={() => onDeleteDoc(doc.id)}
                    disabled={deleting}
                    className="text-telegram-hint hover:text-red-500 active:scale-95 transition-transform disabled:opacity-40"
                    title="Удалить документ"
                  >
                    {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
